import React, { useState } from 'react';
import { AdminLoanDrawer } from 'components/LoanPaymentV2/AdminLoanDrawer';
import { useLoanDrawer } from '../useLoanDrawer';
import {
  ActionList,
  ActionListItem,
  ActionListItemIcon,
  Alert,
  Amount,
  Box,
  Button,
  Checkbox,
  Dropdown,
  DropdownButton,
  DropdownOverlay,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  MoreVerticalIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  TableToolbar,
  TableToolbarActions,
  Text,
  TrashIcon,
  useToast,
} from '@razorpay/blade/components';
import { useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import { useNavigation } from 'context/NavigationContext';
import { routePaths } from 'components/Routes/data';
import { EmptyListView } from 'components/ui/EmptyListView/EmptyListView';
import { LoanDataArraySchemaContract } from 'schemas/LoanDataArraySchema';
import { LOAN_STATUS } from '../constants';
import { useLoanData } from '../useLoanData';

type ActiveLoan = LoanDataArraySchemaContract['data'][number];

export const ActiveLoansTable = () => {
  const navigate = useNavigation();
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const { loanId: activeLoanId, openDrawer, closeDrawer } = useLoanDrawer();

  const { data, isFetching } = useLoanData(LOAN_STATUS.ACTIVE);
  const { data: pendingData } = useLoanData(LOAN_STATUS.PENDING, { staleTime: 60000 });

  const loans = data?.data ?? [];
  const isEmpty = !isFetching && loans.length === 0;
  const pendingCount = pendingData?.total ?? 0;

  const selectedCount = selectedIds.size;
  const allChecked = loans.length > 0 && selectedCount === loans.length;

  const toggleAll = (checked: boolean) =>
    setSelectedIds(checked ? new Set(loans.map((l) => l.id)) : new Set());

  const toggleOne = (id: number, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      checked ? next.add(id) : next.delete(id);
      return next;
    });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.loans.bulkDeleteLoans(Array.from(selectedIds));
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['LOANS']);
      setIsDeleteOpen(false);
      setSelectedIds(new Set());
      showToast({
        content: selectedCount > 1 ? `${selectedCount} loans deleted.` : 'Loan deleted.',
        color: 'neutral',
        duration: 3000,
      });
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to delete loan(s). Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  return (
    <>
      {pendingCount > 0 && (
        <Alert
          marginBottom="spacing.7"
          color="information"
          title={`${pendingCount} pending request${pendingCount > 1 ? 's' : ''} need your approval`}
          description="Employees have requested a loan/advance. Please review to approve or reject."
          isFullWidth
          isDismissible={false}
          actions={{
            primary: {
              text: 'View Requests',
              onClick: () => navigate.navigate(routePaths.loanPaymentV2.root),
            },
          }}
        />
      )}

      <Table<ActiveLoan>
        data={{ nodes: loans }}
        isLoading={isFetching}
        gridTemplateColumns="40px 1.2fr 1fr 0.9fr 0.9fr 0.9fr 0.9fr"
        toolbar={
          <TableToolbar title={`Showing ${loans.length} Items`}>
            <TableToolbarActions>
              <Dropdown>
                <DropdownButton
                  isDisabled={selectedCount === 0}
                  variant="tertiary"
                  icon={MoreVerticalIcon}
                />
                <DropdownOverlay>
                  <ActionList>
                    {[
                      <ActionListItem
                        key="delete"
                        title="Delete"
                        value="delete"
                        intent="negative"
                        isDisabled={selectedCount === 0}
                        leading={<ActionListItemIcon icon={TrashIcon} />}
                        onClick={() => setIsDeleteOpen(true)}
                      />,
                    ]}
                  </ActionList>
                </DropdownOverlay>
              </Dropdown>
            </TableToolbarActions>
          </TableToolbar>
        }>
        {(tableData) => (
          <>
            <TableHeader>
              <TableHeaderRow>
                <TableHeaderCell>
                  <Checkbox
                    isChecked={allChecked}
                    isIndeterminate={selectedCount > 0 && !allChecked}
                    onChange={({ isChecked }) => toggleAll(isChecked)}>
                    {''}
                  </Checkbox>
                </TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Purpose</TableHeaderCell>
                <TableHeaderCell textAlign="right">Principal</TableHeaderCell>
                <TableHeaderCell textAlign="right">Outstanding</TableHeaderCell>
                <TableHeaderCell textAlign="right">EMI Amount</TableHeaderCell>
                <TableHeaderCell textAlign="right">Progress</TableHeaderCell>
              </TableHeaderRow>
            </TableHeader>
            <TableBody>
              {tableData.map((loan) => {
                const isChecked = selectedIds.has(loan.id);
                return (
                  <TableRow key={loan.id} item={loan} onClick={() => openDrawer(loan.id)}>
                    <TableCell>
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          isChecked={isChecked}
                          onChange={({ isChecked: c }) => toggleOne(loan.id, c)}>
                          {''}
                        </Checkbox>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexDirection="column">
                        <Text size="small">{loan.name ?? '—'}</Text>
                        {loan.employee_id && (
                          <Text size="xsmall" color="surface.text.gray.muted">
                            {loan.employee_id}
                          </Text>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Text size="small" truncateAfterLines={1}>
                        {loan.remarks ?? '—'}
                      </Text>
                    </TableCell>
                    <TableCell textAlign="right">
                      <Amount value={loan.principal} size="small" />
                    </TableCell>
                    <TableCell textAlign="right">
                      <Amount value={loan.remaining_amount_to_be_paid ?? 0} size="small" />
                    </TableCell>
                    <TableCell textAlign="right">
                      <Amount value={loan.monthly_total_emi ?? 0} size="small" />
                    </TableCell>
                    <TableCell textAlign="right">
                      <Box display="flex" flexDirection="column" alignItems="flex-end">
                        <Text size="small" weight="semibold">
                          {`${loan.duration_completed ?? 0}/${loan.tenure}`}
                        </Text>
                        <Text size="xsmall" color="surface.text.gray.muted">
                          EMI terms
                        </Text>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>

      {isEmpty && (
        <EmptyListView
          title="No active loans"
          description={
            <Text color="surface.text.gray.muted" textAlign="center">
              No active loans found for your organization.
            </Text>
          }
        />
      )}
      <Modal isOpen={isDeleteOpen} onDismiss={() => setIsDeleteOpen(false)} size="small">
        <ModalHeader
          title="Delete Loan?"
          subtitle={`Permanently deleting ${selectedCount} Loan${selectedCount > 1 ? 's' : ''}`}
        />
        <ModalBody>
          <Text size="medium" color="surface.text.gray.subtle">
            This will permanently delete the selected loan. The records will not appear anywhere
            after deletion.
          </Text>
        </ModalBody>
        <ModalFooter>
          <Box display="flex" gap="spacing.4" width="100%">
            <Button variant="secondary" isFullWidth onClick={() => setIsDeleteOpen(false)}>
              Cancel
            </Button>
            <Button
              color="negative"
              isFullWidth
              isLoading={deleteMutation.isLoading}
              onClick={() => deleteMutation.mutate()}>
              Delete
            </Button>
          </Box>
        </ModalFooter>
      </Modal>

      <AdminLoanDrawer isOpen={!!activeLoanId} onDismiss={closeDrawer} loanId={activeLoanId} />
    </>
  );
};
